import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, Popper } from "@mui/material";
import { CircleNotch } from "@phosphor-icons/react";
import { X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { BASE_API_URL_V2 } from "@/constants";
import fpFetch from "@/services/fpFetch";
import openFile from "@/components/FpAppV4/services/openFile";
import { useViewerTimeZone } from "@/hooks/useViewerTimeZone";
import { useIsPhoneViewport } from "@/hooks/useViewportTier";
import { formatInstant } from "@/utils/temporal";
import type {
  DocumentLineage,
  LineageMember,
} from "./services/useDocumentMetadata";
import {
  isCopyOf,
  noteOf,
  statusOf,
  summaryOf,
  versionBlocks,
  type VersionBlock,
} from "./lineageStatus";
import { VersionMarks } from "./VersionMarks";
import {
  clearProductTimeout,
  setProductTimeout,
  type ProductTimer,
} from "@/utils/productTimers";

// The Versions section of a document's Details (#11956): every copy and
// revision of the open document, newest first, and a Compare that shows what
// changed between two revisions. Nothing here hides or moves a document:
// SharePoint copies stay exactly where the folder structure puts them, and
// this is the view that ties them back together.
//
// Revisions are the spine of the list, on a rail, newest first, and copies
// branch off the revision they copy. A member is one line: its status and
// its date; the open document is the filled node. Everything else — the full filename, the folder, the reason a
// revision is linked, and Compare — lives on a card that opens beside the
// row on hover or focus and stays while the pointer is on the row or the
// card, or while focus is inside it, so a keyboard tabs from the row into
// Compare. A click on a row opens that document. Compare opens the diff in
// a dialog of its own rather than inside the panel. Members of one lineage
// share a filename and most of their path, so the row shows neither. At
// phone width there is no hover, so the card's content sits under its row.

interface DiffHunk {
  removed: string[];
  added: string[];
}

interface VersionsDiff {
  before: { blob_id: string; filename: string };
  after: { blob_id: string; filename: string };
  removed_count: number;
  added_count: number;
  similarity: number;
  hunks: DiffHunk[];
  hunks_truncated: boolean;
}

function locationOf(member: LineageMember): string {
  return member.filesystem_path.join(" / ");
}

// Two members with the same content have nothing to compare; a copy of the
// open document is not offered a Compare, a different revision is.
function isComparable(member: LineageMember, current: LineageMember | null) {
  if (!current || member.blob_id === current.blob_id) return false;
  return member.content_group !== current.content_group;
}

function useVersionsDiff(currentId: string, otherId: string | null) {
  return useQuery({
    queryKey: ["documentVersionsDiff", currentId, otherId],
    queryFn: async (): Promise<VersionsDiff> => {
      if (otherId === null) throw new Error("Nothing to compare");
      const url = new URL(
        `${BASE_API_URL_V2}/api/v2/rms/documents/${currentId}/versions/diff`
      );
      url.searchParams.set("other", otherId);
      const response = await fpFetch(url.toString());
      if (!response.ok) {
        throw new Error(`Failed to compare versions: ${response.status}`);
      }
      return response.json();
    },
    enabled: Boolean(otherId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

function DiffView({
  currentId,
  otherId,
}: {
  currentId: string;
  otherId: string;
}) {
  const { data, isLoading, error } = useVersionsDiff(currentId, otherId);
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-2 text-sm text-gray-500">
        <CircleNotch className="w-4 h-4 animate-spin" />
        Comparing
      </div>
    );
  }
  if (error || !data) {
    return (
      <p className="py-2 text-sm text-red-600">Can't compare these versions.</p>
    );
  }
  const changed = data.removed_count + data.added_count;
  // Floor, so only two identical texts read as 100% similar.
  const similarity = Math.floor(data.similarity * 100);
  return (
    <div className="rounded-[10px] border border-gray-200 overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-100 flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-gray-700">
          {changed === 0
            ? "No text changes"
            : `${changed} line${changed === 1 ? "" : "s"} changed`}
        </span>
        <span className="text-xs font-mono text-[#6E7480]">
          {similarity}% similar
        </span>
      </div>
      {data.hunks.length > 0 && (
        <div className="max-h-[60vh] overflow-y-auto text-sm leading-6">
          {data.hunks.map((hunk, i) => (
            <div
              key={i}
              className={i > 0 ? "border-t border-gray-100" : undefined}
            >
              {hunk.removed.map((line, j) => (
                <div
                  key={`r${j}`}
                  className="px-3 bg-red-50 text-red-900 break-words"
                >
                  <span className="select-none text-red-400 mr-2">−</span>
                  {line}
                </div>
              ))}
              {hunk.added.map((line, j) => (
                <div
                  key={`a${j}`}
                  className="px-3 bg-green-50 text-green-900 break-words"
                >
                  <span className="select-none text-green-500 mr-2">+</span>
                  {line}
                </div>
              ))}
            </div>
          ))}
          {data.hunks_truncated && (
            <div className="px-3 py-1.5 text-xs text-gray-500 border-t border-gray-100">
              Showing the first {data.hunks.length} changes
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// The diff in a dialog of its own: the panel is too narrow to read two
// versions of a paragraph, and a reader comparing wants the whole change in
// front of them, then to dismiss it.
function CompareDialog({
  current,
  other,
  otherLabel,
  viewerZone,
  onClose,
}: {
  current: LineageMember;
  other: LineageMember;
  otherLabel: string;
  viewerZone: string;
  onClose: () => void;
}) {
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      aria-labelledby="compare-versions-title"
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          width: "720px",
          maxWidth: "calc(100vw - 32px)",
          margin: "16px",
        },
      }}
    >
      <div className="p-4 md:p-5 flex flex-col gap-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2
              id="compare-versions-title"
              className="text-lg font-semibold text-gray-900"
            >
              Compare versions
            </h2>
            <p className="text-sm text-gray-500 break-words">
              {otherLabel} (
              {formatInstant(other.uploaded_at, viewerZone, "medium")}) against
              this document (
              {formatInstant(current.uploaded_at, viewerZone, "medium")})
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 hover:bg-gray-100 rounded-full transition-colors cursor-pointer shrink-0"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <DiffView currentId={current.blob_id} otherId={other.blob_id} />
      </div>
    </Dialog>
  );
}

// How long the pointer may be off both the row and the card before the card
// closes: long enough to cross the gap between them, short enough that the
// card is gone by the time the pointer settles elsewhere.
export const CLOSE_DELAY_MS = 250;
export const OPEN_DELAY_MS = 150;

// The card beside a row: the filename, the folder, the reason a revision is
// linked (a copy needs none: it is a copy), and Compare. Inline, under the
// row at phone width, it is the same content without the popover's frame.
function MemberCard({
  member,
  isCopy,
  comparable,
  inline,
  onCompare,
}: {
  member: LineageMember;
  isCopy: boolean;
  comparable: boolean;
  inline: boolean;
  onCompare: () => void;
}) {
  const location = locationOf(member);
  const reason = isCopy ? [] : member.evidence;
  return (
    <div
      role={inline ? "group" : "dialog"}
      aria-label={member.filename}
      className={
        inline
          ? "flex flex-col gap-2 pb-2"
          : "w-[292px] max-w-[calc(100vw-24px)] rounded-[10px] border border-gray-200 bg-white shadow-[0_8px_24px_rgba(17,24,39,0.12)] px-3.5 py-3 flex flex-col gap-2.5"
      }
    >
      <div className="text-sm font-medium text-gray-900 break-words">
        {member.filename}
      </div>
      {location && (
        <div className="flex flex-col gap-0.5">
          <div className="text-[11px] leading-4 font-medium uppercase tracking-wide text-gray-500">
            Folder
          </div>
          <div className="text-[13px] leading-[18px] text-gray-900 break-words">
            {location}
          </div>
        </div>
      )}
      {reason.length > 0 && (
        <div className="flex flex-col gap-0.5">
          <div className="text-[11px] leading-4 font-medium uppercase tracking-wide text-gray-500">
            Reason
          </div>
          <ul className="flex flex-col gap-1">
            {reason.map((line, i) => (
              <li
                key={i}
                className="text-[13px] leading-[18px] text-gray-900 break-words"
              >
                {line}
              </li>
            ))}
          </ul>
        </div>
      )}
      {comparable && (
        <div className="flex gap-1.5 pt-0.5">
          <button
            type="button"
            onClick={onCompare}
            className="rounded-md px-2.5 py-1 text-xs border border-gray-200 text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
          >
            Compare
          </button>
        </div>
      )}
    </div>
  );
}

function MemberRow({
  member,
  block,
  current,
  note,
  isFirst,
  isLast,
  phone,
  onCompare,
  onOpenVersion,
  viewerZone,
}: {
  member: LineageMember;
  block: VersionBlock;
  current: LineageMember | null;
  // The likelihood note this row shows, already decided by the panel: null
  // for a fact, and for the open document's own row when another row shows
  // the same note.
  note: string | null;
  isFirst: boolean;
  isLast: boolean;
  phone: boolean;
  onCompare: () => void;
  onOpenVersion?: () => void;
  viewerZone: string;
}) {
  const isCopy = isCopyOf(member, block);
  const status = statusOf(member, block);
  const itemRef = useRef<HTMLLIElement | null>(null);
  const rowRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const openTimer = useRef<ProductTimer | null>(null);
  const closeTimer = useRef<ProductTimer | null>(null);

  const clearTimers = useCallback(() => {
    if (openTimer.current !== null) clearProductTimeout(openTimer.current);
    if (closeTimer.current !== null) clearProductTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  }, []);
  useEffect(() => clearTimers, [clearTimers]);

  // Escape closes the card however it was opened, a hover included.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        clearTimers();
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, clearTimers]);

  // Hover (or focus) opens after a beat and closes after a beat, so the
  // pointer can cross from the row to the card, and a pass over the list
  // opens nothing.
  const pointerIn = () => {
    // The open document is the point of view: no card of its own.
    if (member.is_current || phone) return;
    if (closeTimer.current !== null) clearProductTimeout(closeTimer.current);
    closeTimer.current = null;
    if (open || openTimer.current !== null) return;
    openTimer.current = setProductTimeout(() => {
      openTimer.current = null;
      setOpen(true);
    }, OPEN_DELAY_MS);
  };
  const pointerOut = () => {
    if (openTimer.current !== null) clearProductTimeout(openTimer.current);
    openTimer.current = null;
    closeTimer.current = setProductTimeout(() => {
      closeTimer.current = null;
      setOpen(false);
    }, CLOSE_DELAY_MS);
  };
  // Focus moving between the row and the card keeps the card open; leaving
  // both closes it.
  const focusOut = (event: React.FocusEvent<HTMLElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && itemRef.current?.contains(next)) return;
    pointerOut();
  };
  const close = () => {
    clearTimers();
    setOpen(false);
  };

  const when = formatInstant(member.uploaded_at, viewerZone, "medium");
  // Copies have nothing to compare; a version whose content differs from
  // the open document's does.
  const comparable = !isCopy && isComparable(member, current);
  // A click on another version opens that document; the open one is inert.
  const openDocument = () => {
    if (member.is_current) return;
    close();
    openFile({
      fileId: member.blob_id,
      fileName: member.filename,
      fileType: member.blob_kind,
    });
    onOpenVersion?.();
  };
  const compare = () => {
    close();
    onCompare();
  };

  // The node and the stub sit on the row's line; at phone width the card
  // follows under it, so the marks are placed off the row's height rather
  // than the item's.
  const rowHeight = "h-[34px]";
  // Half the row's height: where the node sits and the rail starts or ends.
  const ROW_CENTER = "17px";
  const popover = !phone && !member.is_current;

  return (
    <li
      ref={itemRef}
      className="relative"
      onFocus={pointerIn}
      onBlur={focusOut}
    >
      <VersionMarks
        isCopy={isCopy}
        isFirst={isFirst}
        isLast={isLast}
        filled={member.is_current}
        center={ROW_CENTER}
      />
      <button
        ref={rowRef}
        type="button"
        aria-haspopup={popover ? "dialog" : undefined}
        aria-expanded={popover ? open : undefined}
        aria-label={
          // The note is in the name too, so a screen reader hears a
          // suggestion where a sighted reader sees one.
          member.is_current
            ? `${status}${note ? `, ${note}` : ""}, ${member.filename}, this document`
            : `${status}${note ? `, ${note}` : ""}, open ${member.filename}`
        }
        onMouseEnter={pointerIn}
        onMouseLeave={pointerOut}
        onClick={openDocument}
        className={`w-full flex items-center gap-2.5 ${rowHeight} pr-1 rounded-md text-left transition-colors ${
          isCopy ? "pl-8" : "pl-[22px]"
        } ${member.is_current ? "cursor-default" : "cursor-pointer"} ${
          open ? "bg-gray-50" : member.is_current ? "" : "hover:bg-gray-50"
        }`}
      >
        <span className="flex-grow min-w-0 flex items-baseline gap-2 flex-wrap">
          <span
            className={`leading-5 ${
              isCopy ? "text-[13px] text-gray-700" : "text-sm text-gray-900"
            } ${(!isCopy && block.back === 0) || member.is_current ? "font-medium" : ""}`}
          >
            {status}
          </span>
          {/* Quiet text rather than a pill. */}
          {note !== null && (
            <span className="text-xs leading-4 text-gray-500">{`· ${note}`}</span>
          )}
        </span>
        <span className="shrink-0 text-xs leading-4 text-gray-500">{when}</span>
      </button>
      {phone && !member.is_current && (
        <div className={isCopy ? "pl-8" : "pl-[22px]"}>
          <MemberCard
            member={member}
            isCopy={isCopy}
            comparable={comparable}
            inline
            onCompare={compare}
          />
        </div>
      )}
      {popover && (
        // In the document rather than a portal, so the card follows the row
        // in tab order; fixed positioning keeps the panel's overflow from
        // clipping it.
        <Popper
          open={open}
          anchorEl={rowRef.current}
          placement="left-start"
          disablePortal
          popperOptions={{ strategy: "fixed" }}
          modifiers={[
            { name: "offset", options: { offset: [0, 10] } },
            // The viewport is the boundary, not the panel's scroll box the
            // card now sits in: beside the row means outside that box.
            {
              name: "flip",
              options: {
                boundary: document.body,
                fallbackPlacements: ["bottom"],
              },
            },
            {
              name: "preventOverflow",
              options: { boundary: document.body, padding: 12 },
            },
          ]}
          style={{ zIndex: 1400 }}
        >
          <div onMouseEnter={pointerIn} onMouseLeave={pointerOut}>
            <MemberCard
              member={member}
              isCopy={isCopy}
              comparable={comparable}
              inline={false}
              onCompare={compare}
            />
          </div>
        </Popper>
      )}
    </li>
  );
}

const DocumentVersionsSection: React.FC<{
  lineage: DocumentLineage;
  // Told after a version is opened; the host uses it to close the Details
  // pane at phone width, where the pane covers the document.
  onOpenVersion?: () => void;
}> = ({ lineage, onOpenVersion }) => {
  const viewerZone = useViewerTimeZone();
  const phone = useIsPhoneViewport();
  const [comparingId, setComparingId] = useState<string | null>(null);
  const current = lineage.members.find((m) => m.is_current) ?? null;
  const blocks = versionBlocks(lineage);
  const rows = blocks.flatMap((block) =>
    [block.head, ...block.copies].map((member) => ({ member, block }))
  );
  const comparingRow =
    comparingId === null
      ? undefined
      : rows.find((r) => r.member.blob_id === comparingId);
  // The open document's own row keeps its note only when no other row shows
  // the same one: in a two-document lineage both memberships hold the one
  // link's probability, and the same number on both rows read it twice,
  // once on the document the reader is looking at. Keyed on the duplicate
  // rather than on the row being the open document's, because a document
  // joined by text into a lineage of facts is the only suggested member,
  // and hiding its note left its panel reading as all facts.
  const notedElsewhere = new Set<string>();
  for (const { member } of rows) {
    const note = noteOf(member);
    if (!member.is_current && note !== null) notedElsewhere.add(note);
  }
  const noteFor = (member: LineageMember): string | null => {
    const note = noteOf(member);
    if (note === null) return null;
    return member.is_current && notedElsewhere.has(note) ? null : note;
  };

  return (
    <section aria-label="Versions" className="border-b border-gray-100 pb-2">
      <div className="flex items-baseline justify-between mb-0.5">
        <h3 className="text-xs font-medium text-gray-500">Versions</h3>
        <span className="text-xs text-gray-500">{summaryOf(lineage)}</span>
      </div>
      <ol>
        {rows.map(({ member, block }, i) => (
          <MemberRow
            key={member.blob_id}
            member={member}
            block={block}
            current={current}
            note={noteFor(member)}
            isFirst={i === 0}
            isLast={i === rows.length - 1}
            phone={phone}
            onCompare={() => setComparingId(member.blob_id)}
            onOpenVersion={onOpenVersion}
            viewerZone={viewerZone}
          />
        ))}
      </ol>
      {comparingRow && current && (
        <CompareDialog
          current={current}
          other={comparingRow.member}
          otherLabel={statusOf(comparingRow.member, comparingRow.block)}
          viewerZone={viewerZone}
          onClose={() => setComparingId(null)}
        />
      )}
    </section>
  );
};

export default DocumentVersionsSection;
